
export type PurchasingTarget = {
  basis: "last" | "best";
  discountPct: number;
  revision: number;
  origin: "company" | "own" | "history";
  asOf: string;
};
export type TargetVersion = { revision: number; basis: "last" | "best"; discount_pct: string; actor: string; created_at: string };
export type TargetResponse = { current: TargetVersion | null; history: TargetVersion[]; can_manage: boolean };
export function targetFromVersion(version: TargetVersion | null): PurchasingTarget {
  return { basis: version?.basis ?? "last", discountPct: Number(version?.discount_pct ?? 0), revision: version?.revision ?? 0,
    origin: version ? "company" : "history", asOf: new Date().toISOString().slice(0, 10) };
}
type Fraction = { n: bigint; d: bigint };
function decimal(value: string | number | null | undefined): Fraction | null {
  if (value === null || value === undefined || !Number.isFinite(Number(value))) return null;
  const match = String(value).trim().match(/^([+]?)(\d+)(?:\.(\d*))?(?:e([+-]?\d+))?$/i);
  if (!match) return null;
  const exponent = Number(match[4] ?? 0) - (match[3]?.length ?? 0);
  if (Math.abs(exponent) > 100) return null;
  const digits = BigInt(match[2] + (match[3] ?? ""));
  return exponent >= 0 ? { n: digits * 10n ** BigInt(exponent), d: 1n } : { n: digits, d: 10n ** BigInt(-exponent) };
}
function cents(value: Fraction): number | null {
  const rounded = (value.n * 200n + value.d) / (2n * value.d);
  return rounded > BigInt(Number.MAX_SAFE_INTEGER) ? null : Number(rounded) / 100;
}
export function targetPrice(reference: string | number | null | undefined, discount: number): number | null {
  const price = decimal(reference), pct = decimal(discount);
  if (!price || price.n <= 0n || !pct || pct.n > 100n * pct.d) return null;
  return cents({ n: price.n * (100n * pct.d - pct.n), d: price.d * 100n * pct.d });
}
export function multiplyRounded(price: string | number, quantity: string | number): number | null {
  const a = decimal(price), b = decimal(quantity);
  return a && b ? cents({ n: a.n * b.n, d: a.d * b.d }) : null;
}
export function positiveDifferenceAmount(price: string | number, target: number, quantity: string | number): number | null {
  const a = decimal(price), b = decimal(target), q = decimal(quantity);
  if (!a || !b || !q) return null;
  const difference = a.n * b.d - b.n * a.d;
  return difference <= 0n ? 0 : cents({ n: difference * q.n, d: a.d * b.d * q.d });
}
export function targetLabel(value: PurchasingTarget): string {
  return `${value.basis === "last" ? "Letzter" : "Bester"} Einkauf · 12 Monate · ${value.discountPct ? `${value.discountPct.toLocaleString("de-AT")} % Abschlag` : "ohne zusätzlichen Abschlag"}`;
}
export function isPurchasingTarget(value: unknown): value is PurchasingTarget {
  if (!value || typeof value !== "object") return false;
  const target = value as PurchasingTarget;
  return ["last", "best"].includes(target.basis) && ["company", "own", "history"].includes(target.origin)
    && Number.isFinite(target.discountPct) && target.discountPct >= 0 && target.discountPct <= 100
    && Number.isInteger(target.revision) && target.revision >= 0 && /^\d{4}-\d{2}-\d{2}$/.test(target.asOf);
}
